/**
 * useAuth — удобный фасад над `atoms/auth/`.
 *
 * Один импорт вместо ковыряния по атомам в каждом компоненте:
 *   const {user, token, permission, isAuthenticated,
 *          setSession, setToken, selectPermission, logout} = useAuth();
 *
 * Смена сессии (login/logout) чистит кэш React Query. Иначе следующий
 * пользователь на том же устройстве видит данные предыдущего: список
 * контекстов в пикере, права, заказы — всё это лежит под ключами без
 * привязки к юзеру и переживает выход.
 */

import {useAtomValue, useSetAtom} from 'jotai';

import type {Permission, User} from '@services/auth';
import {queryClient} from '@queries/queryClient';
import {
  isAuthenticatedAtom,
  logoutAtom,
  selectedPermissionAtom,
  tokenAtom,
  userAtom,
} from '@atoms/auth';

export type UseAuthResult = {
  user: User | null;
  token: string | null;
  permission: Permission | null;
  isAuthenticated: boolean;
  setSession: (token: string, user: User) => void;
  setToken: (token: string) => void;
  selectPermission: (permission: Permission) => void;
  logout: () => void;
};

export const useAuth = (): UseAuthResult => {
  const user = useAtomValue(userAtom);
  const token = useAtomValue(tokenAtom);
  const permission = useAtomValue(selectedPermissionAtom);
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  const setUserAtom = useSetAtom(userAtom);
  const setTokenAtom = useSetAtom(tokenAtom);
  const setPermissionAtom = useSetAtom(selectedPermissionAtom);
  const triggerLogout = useSetAtom(logoutAtom);

  const setSession = (nextToken: string, nextUser: User) => {
    // Заходят под другим аккаунтом — данные прошлого не наследуем.
    queryClient.clear();
    setTokenAtom(nextToken);
    setUserAtom(nextUser);
  };

  const setToken = (nextToken: string) => {
    setTokenAtom(nextToken);
  };

  const selectPermission = (nextPermission: Permission) => {
    setPermissionAtom(nextPermission);
  };

  const logout = () => {
    triggerLogout();
    queryClient.clear();
  };

  return {user, token, permission, isAuthenticated, setSession, setToken, selectPermission, logout};
};
